<template>
  <div class="event-calendar-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Academic Event Calendar</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ reportItems.length }} Events
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
      <p class="mt-3 font-weight-bold">Loading academic event calendar...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3">Retry</b-button>
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-3">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img">
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName }}<span v-if="campusBranch" class="text-muted"> - {{ campusBranch }}</span>
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Academic Event Calendar</span>
              <span class="date-range-text">
                <strong>{{ eventForLabel }}</strong> |
                {{ filterSummary }} |
                {{ dateRangeLabel }} |
                {{ reportItems.length }} Event{{ reportItems.length === 1 ? "" : "s" }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="eventsByMonth.length === 0" class="empty-state">
        <feather-icon icon="CalendarIcon" size="40" class="text-muted mb-2" />
        <p>No events found for the selected criteria</p>
      </div>

      <section
        v-for="monthBlock in eventsByMonth"
        :key="monthBlock.key"
        class="month-block"
      >
        <div class="month-heading">
          <span class="month-heading-text">{{ monthBlock.label }}</span>
          <div class="month-heading-right">
            <span v-if="monthBlock.hijriLabel" class="month-hijri-label">
              {{ monthBlock.hijriLabel }}
            </span>
            <span class="month-event-count">
              {{ monthBlock.events.length }}
              event{{ monthBlock.events.length === 1 ? "" : "s" }}
            </span>
          </div>
        </div>

        <div class="table-container">
          <table class="calendar-events-table">
            <colgroup>
              <col class="col-event-col">
              <col class="col-date-col">
            </colgroup>
            <tbody>
              <tr
                v-for="(item, index) in monthBlock.events"
                :key="`${monthBlock.key}-${item.id || index}`"
              >
                <td class="event-cell">
                  <div class="event-title">{{ item.title || "Untitled Event" }}</div>
                  <div v-if="item.description" class="event-description">
                    {{ item.description }}
                  </div>
                  <div v-if="item.location" class="event-location">
                    {{ formatLocation(item.location) }}
                  </div>
                </td>
                <td class="date-cell">
                  {{ formatDateRange(item.start, item.end) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

const HIJRI_MONTH_NAMES = {
  1: "Muharram",
  2: "Safar",
  3: "Rabi al-Awwal",
  4: "Rabi al-Thani",
  5: "Jumada al-Awwal",
  6: "Jumada al-Thani",
  7: "Rajab",
  8: "Sha'ban",
  9: "Ramadan",
  10: "Shawwal",
  11: "Dhul Qadah",
  12: "Dhul Hijjah",
};

export default {
  name: "EventCalendarReportPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: "",
      eventFor: "Students",
      classID: 0,
      sectionID: 0,
      departmentID: 0,
      dtFrom: "",
      dtTo: "",
      classLabel: "",
      sectionLabel: "",
      departmentLabel: "",
    };
  },
  computed: {
    isStudentReport() {
      return String(this.eventFor || "").toLowerCase() === "students";
    },
    eventForLabel() {
      return this.eventFor || "Students";
    },
    dateRangeLabel() {
      if (this.dtFrom && this.dtTo) {
        return `${this.formatDate(this.dtFrom)} – ${this.formatDate(this.dtTo)}`;
      }
      if (this.dtFrom) {
        return `From ${this.formatDate(this.dtFrom)}`;
      }
      if (this.dtTo) {
        return `Till ${this.formatDate(this.dtTo)}`;
      }
      return "All Dates";
    },
    filterSummary() {
      if (this.isStudentReport) {
        const cls =
          this.classLabel ||
          (Number(this.classID) === 0 ? "All Classes" : "");
        const sec =
          this.sectionLabel ||
          (Number(this.sectionID) === 0 ? "All Sections" : "");
        if (cls && sec) return `${cls} / ${sec}`;
        return cls || sec || "All Classes / All Sections";
      }
      return (
        this.departmentLabel ||
        (Number(this.departmentID) === 0 ? "All Departments" : "")
      );
    },
    eventsByMonth() {
      const groups = {};

      this.reportItems.forEach((item) => {
        const sortDate = this.parseDate(item.start) || this.parseDate(item.end);
        if (!sortDate) return;

        const key = `${sortDate.getFullYear()}-${String(
          sortDate.getMonth() + 1
        ).padStart(2, "0")}`;
        const label = sortDate.toLocaleDateString("en-GB", {
          month: "long",
          year: "numeric",
        });

        if (!groups[key]) {
          groups[key] = {
            key,
            label,
            sortDate: new Date(sortDate.getFullYear(), sortDate.getMonth(), 1),
            events: [],
          };
        }

        groups[key].events.push(item);
      });

      return Object.values(groups)
        .sort((a, b) => a.sortDate - b.sortDate)
        .map((group) => ({
          ...group,
          hijriLabel: this.buildHijriMonthLabel(group.sortDate),
          events: group.events.sort((a, b) => {
            const aDate = this.parseDate(a.start) || this.parseDate(a.end);
            const bDate = this.parseDate(b.start) || this.parseDate(b.end);
            if (!aDate && !bDate) return 0;
            if (!aDate) return 1;
            if (!bDate) return -1;
            return aDate - bDate;
          }),
        }));
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || this.$store.state.userData.cId;
      this.eventFor = query.eventFor || "Students";
      this.classID = Number(query.classID || 0);
      this.sectionID = Number(query.sectionID || 0);
      this.departmentID = Number(query.departmentID || 0);
      this.dtFrom = query.dtFrom || "";
      this.dtTo = query.dtTo || "";
      this.classLabel = query.classLabel || "";
      this.sectionLabel = query.sectionLabel || "";
      this.departmentLabel = query.departmentLabel || "";

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const response = await axios.get(`${this.$store.state.domain}campuses`, {
          params: {
            db: this.db,
            cId: this.cID,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          const info = match || response.data.data[0];
          if (info) {
            this.campusName = info.name;
            this.campusBranch = this.cID == 0 ? "All Campuses" : info.branch;
            this.schoolLogo = info.logo;
          }
        }
      } catch (e) {
        console.warn("Campus info fetch failed, using fallback from store");
        const currentBranch = this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
        }
      }
    },
    normalizeEvents(payload) {
      let rows = [];
      if (payload?.status === "success" && Array.isArray(payload.data)) {
        rows = payload.data;
      } else if (Array.isArray(payload?.data)) {
        rows = payload.data;
      } else if (Array.isArray(payload)) {
        rows = payload;
      }

      return rows
        .map((item, index) => {
          const event = item.events || item.event || item;
          if (!event || (!event.title && !event.description)) return null;

          return {
            id: event.id || item.id || index,
            title: event.title || "",
            description: event.description || "",
            start: event.start || event.startDate || "",
            end: event.end || event.endDate || "",
            eventFor: event.eventFor || this.eventForLabel,
            location: event.location || "",
          };
        })
        .filter(Boolean);
    },
    async loadReportData() {
      try {
        this.loading = true;
        this.error = null;

        let baseUrl = this.$store.state.domain;
        if (!baseUrl.endsWith("/")) {
          baseUrl += "/";
        }

        const params = {
          db: this.db,
          cID: this.cID,
          eventFor: this.eventFor,
          classID: this.isStudentReport ? this.classID || 0 : 0,
          sectionID: this.isStudentReport ? this.sectionID || 0 : 0,
          departmentID: this.isStudentReport ? 0 : this.departmentID || 0,
        };

        if (this.dtFrom) params.dtFrom = this.dtFrom;
        if (this.dtTo) params.dtTo = this.dtTo;

        const response = await axios.get(
          `${baseUrl}EventsCalendar/EventCalendarReport`,
          {
            params,
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        const payload = response?.data ?? response;
        if (payload?.status === "error") {
          this.error =
            payload.message || "Failed to load event calendar report.";
          this.reportItems = [];
          return;
        }

        this.reportItems = this.normalizeEvents(payload);
      } catch (error) {
        console.error("Failed to load event calendar report:", error);
        this.error = "Network error or server unavailable.";
        this.reportItems = [];
      } finally {
        this.loading = false;
      }
    },
    parseDate(dateString) {
      if (!dateString) return null;
      const date = new Date(dateString);
      return Number.isNaN(date.getTime()) ? null : date;
    },
    getHijriParts(date) {
      if (!date) return null;
      const calendars = ["islamic-umalqura", "islamic"];
      for (const calendar of calendars) {
        try {
          const parts = new Intl.DateTimeFormat("en-GB", {
            calendar,
            month: "numeric",
            year: "numeric",
          }).formatToParts(date);
          const monthNum = Number(
            parts.find((p) => p.type === "month")?.value
          );
          const year = parts.find((p) => p.type === "year")?.value;
          const monthName = HIJRI_MONTH_NAMES[monthNum];
          if (monthName && year) {
            return { month: monthName, year };
          }
        } catch (e) {
          // try next calendar system
        }
      }
      return null;
    },
    buildHijriMonthLabel(monthDate) {
      if (!monthDate) return "";
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const start = this.getHijriParts(firstDay);
      const end = this.getHijriParts(lastDay);
      if (!start) return "";

      if (start.month === end.month && start.year === end.year) {
        return `${start.month} ${start.year} AH`;
      }
      if (start.year === end.year) {
        return `${start.month} – ${end.month} ${start.year} AH`;
      }
      return `${start.month} ${start.year} – ${end.month} ${end.year} AH`;
    },
    formatDate(dateString) {
      const date = this.parseDate(dateString);
      if (!date) return "-";
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    formatDateRange(start, end) {
      const startLabel = this.formatDate(start);
      const endLabel = this.formatDate(end);

      if (startLabel === "-" && endLabel === "-") return "-";
      if (startLabel !== "-" && endLabel === "-") return startLabel;
      if (startLabel === "-" && endLabel !== "-") return endLabel;
      if (startLabel === endLabel) return startLabel;
      return `${startLabel} – ${endLabel}`;
    },
    formatLocation(location) {
      if (!location) return "";
      if (location.startsWith("http://") || location.startsWith("https://")) {
        try {
          const url = new URL(location);
          return url.hostname.replace("www.", "") || location;
        } catch (e) {
          return location;
        }
      }
      return location;
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.event-calendar-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.05rem;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }

    .date-range-text {
      color: #555;
    }
  }
}

.month-block {
  margin-bottom: 1.5rem;
  page-break-inside: avoid;
  border: 1px solid #cbd5e1;
  overflow: hidden;
}

.month-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.65rem 1rem;
  background: #2c3e50;
  color: #ffffff;
  border-bottom: 1px solid #2c3e50;
}

.month-heading-text {
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  text-transform: capitalize;
}

.month-heading-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  text-align: right;
}

.month-hijri-label {
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.2;
}

.month-event-count {
  font-size: 0.72rem;
  font-weight: 600;
  opacity: 0.88;
  white-space: nowrap;
}

.table-container {
  width: 100%;
  overflow: visible;
}

.calendar-events-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  table-layout: fixed;

  .col-event-col {
    width: 72%;
  }

  .col-date-col {
    width: 28%;
  }

  td {
    border: 1px solid #dee2e6;
    padding: 12px 14px;
    box-sizing: border-box;
    vertical-align: middle;
    font-size: 0.95rem;
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.event-cell {
  word-break: break-word;
  text-align: left;
}

.date-cell {
  text-align: center;
  font-weight: 700;
  color: #1e3a8a;
  white-space: nowrap;
  vertical-align: middle;
  border-left: 1px solid #dee2e6;
}

.event-title {
  font-size: 0.98rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.35;
}

.event-description {
  font-size: 0.88rem;
  color: #4b5563;
  line-height: 1.45;
  white-space: pre-wrap;
  margin-top: 0.25rem;
}

.event-location {
  margin-top: 0.35rem;
  font-size: 0.78rem;
  color: #6b7280;
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;
}

@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .event-calendar-report {
    background: white !important;
    padding: 0 !important;
    min-height: auto !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 0.9cm 0.7cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    border-radius: 0 !important;
  }

  .report-header-modern {
    margin-bottom: 14px !important;
    padding-bottom: 10px !important;
    border-bottom: 3px solid #000 !important;

    .school-logo-img {
      height: 85px !important;
      max-width: 200px !important;
    }

    .school-name-full {
      font-size: 1.4rem !important;
    }

    .report-info-line {
      font-size: 0.82rem !important;
    }
  }

  .month-block {
    margin-bottom: 10px !important;
    page-break-inside: avoid;
    border: 1px solid #000 !important;
    overflow: hidden !important;
  }

  .month-heading {
    padding: 7px 12px !important;
    background: #2c3e50 !important;
    color: #fff !important;
    border-bottom: 1px solid #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .month-heading-text {
    font-size: 0.92rem !important;
    color: #fff !important;
    font-weight: 800 !important;
  }

  .month-heading-right {
    align-items: flex-end !important;
  }

  .month-hijri-label {
    font-size: 0.78rem !important;
    color: #fff !important;
    font-weight: 700 !important;
  }

  .month-event-count {
    font-size: 0.68rem !important;
    color: #fff !important;
    opacity: 0.9 !important;
  }

  .table-container {
    width: 100% !important;
    overflow: visible !important;
  }

  .calendar-events-table {
    font-size: 9.5pt !important;
    width: 100% !important;
    page-break-inside: auto !important;

    .col-event-col {
      width: 72% !important;
    }

    .col-date-col {
      width: 28% !important;
    }

    td {
      padding: 8px 12px !important;
      border: 1px solid #000 !important;
      border-top: none !important;
      color: #000 !important;
      line-height: 1.3 !important;
    }

    tbody tr:first-child td {
      border-top: none !important;
    }

    tbody tr:nth-child(even) {
      background: #f8f9fa !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .event-title {
    font-size: 9.5pt !important;
    color: #000 !important;
    font-weight: 700 !important;
  }

  .event-description,
  .event-location {
    font-size: 8.5pt !important;
    color: #333 !important;
  }

  .date-cell {
    color: #1e3a8a !important;
    font-weight: 700 !important;
    white-space: nowrap !important;
    text-align: center !important;
    vertical-align: middle !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
